import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import { helpArticleUrl, helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  fetchAllSegments,
  createSegment,
  getContactFieldDefinitions,
  getInactiveContactsSummary,
  loadTagOptions,
  ContactFieldDefinition,
} from "../../../helpers/audience_helper";
import {
  buildFieldsQueryParam,
  defaultOpForFieldType,
  FieldFilterEntry,
  FieldFilterOp,
  isFieldFilterActive,
  operatorLabel,
  operatorsForFieldType,
  parseInValues,
} from "../../../helpers/fieldFilters";
import { buildSegmentRulesFromAudienceFilter } from "../../../helpers/segmentRules";
import {
  estimateCampaignAudience,
  queueCampaignSend,
  SendCampaignPayload,
} from "../../../helpers/sends_helper";
import type { Campaign } from "../../../helpers/campaigns_helper";
import {
  readTenantTimezone,
  relativeFromNow,
  tenantTimezoneDisplayLabel,
  tomorrowSameWallClockDatetimeLocal,
  isAfterUtcDayEnd,
  formatNextUtcDayResetLocal,
  wallClockFromDatetimeLocal,
  wallClockInTimeZoneToDate,
} from "../../../helpers/tenantTimezone";
import { fetchMe, type MeTenant } from "../../../helpers/profile_helper";
import { getSendingSetup } from "../../../helpers/sending_setup_helper";
import { getWorkspaceSettings } from "../../../helpers/workspace_settings_helper";
import { hasTenantRole } from "../../../utils/tenantRoles";

type TagOption = { value: number; label: string; contacts_count?: number };
type SegmentOption = { value: number; label: string };

type FilterFieldRow = {
  key: string;
  slug: string;
  op: FieldFilterOp;
  value: string;
  valueTo?: string;
};

let filterRowSeq = 0;
const nextFilterRowKey = () => `ff-${++filterRowSeq}`;

type Props = {
  campaign: Campaign;
  onSuccess: (sendId: number) => void;
  onCancel?: () => void;
  /** true = rodapé do modal; false = página dedicada */
  compact?: boolean;
};

const ESTIMATE_DEBOUNCE_MS = 400;

/** Presets de ritmo — quantidade sempre 1 e-mail; só o intervalo muda. */
const INTERVAL_PRESETS = [0, 1, 5, 10, 30, 60, 120, 300, 600] as const;

function formatIntervalOption(
  seconds: number,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (seconds === 0) {
    return t("sending.form.intervalFastest");
  }
  if (seconds >= 60 && seconds % 60 === 0) {
    return t("sending.form.intervalOptionMinutes", {
      minutes: seconds / 60,
    });
  }
  return t("sending.form.intervalOption", { seconds });
}

/** Estimativa leiga de duração (intervalo × destinatários − 1). */
function formatDurationHint(
  totalSeconds: number,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (totalSeconds < 60) {
    return t("sending.form.durationSeconds", { seconds: totalSeconds });
  }
  if (totalSeconds < 3600) {
    return t("sending.form.durationMinutes", {
      minutes: Math.ceil(totalSeconds / 60),
    });
  }
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.ceil((totalSeconds % 3600) / 60);
  if (mins === 0) {
    return t("sending.form.durationHours", { hours });
  }
  return t("sending.form.durationHoursMinutes", { hours, minutes: mins });
}

/**
 * Formulário de disparo (modal ou página) — audiência, estimativa, agendamento,
 * amostra parcial, ritmo e data limite.
 */
const SendCampaignForm = ({
  campaign,
  onSuccess,
  onCancel,
  compact = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const tenantTimezone = useMemo(() => readTenantTimezone(), []);
  const timezoneLabel = useMemo(
    () => tenantTimezoneDisplayLabel(tenantTimezone, t),
    [tenantTimezone, t]
  );
  const sendListHelpHref = helpUrlForRoute(
    "/sends",
    i18n.language || "pt-BR"
  );

  const [mode, setMode] = useState<"all" | "tags" | "segment" | "filter">("all");
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTags, setFilterTags] = useState<TagOption[]>([]);
  const [filterTagMatch, setFilterTagMatch] = useState<"and" | "or">("or");
  const [filterFieldRows, setFilterFieldRows] = useState<FilterFieldRow[]>([]);
  const [filterFieldDefs, setFilterFieldDefs] = useState<ContactFieldDefinition[]>([]);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [segmentOptions, setSegmentOptions] = useState<SegmentOption[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<SegmentOption | null>(
    null
  );
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [deadlineLocal, setDeadlineLocal] = useState("");
  const [partialSample, setPartialSample] = useState(false);
  const [sampleByEngagement, setSampleByEngagement] = useState(true);
  const [resumeRemainderTomorrow, setResumeRemainderTomorrow] = useState(true);
  const [resumeScheduledLocal, setResumeScheduledLocal] = useState("");
  const [excludedTagOption, setExcludedTagOption] = useState<TagOption | null>(
    null
  );
  const [intervalSeconds, setIntervalSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorCode, setFormErrorCode] = useState<string | null>(null);
  const [estimated, setEstimated] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [ramp, setRamp] = useState<MeTenant["sending_ramp"] | null>(null);
  const [pace, setPace] = useState<MeTenant["sending_pace"] | null>(null);
  const [replyPreview, setReplyPreview] = useState<{
    fromEmail: string | null;
    replyTo: string | null;
  } | null>(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  /** Hint P3: base fria alta ao escolher “todos” — sugere higiene / segmento. */
  const [showInactiveHint, setShowInactiveHint] = useState(false);
  const [saveSegmentOpen, setSaveSegmentOpen] = useState(false);
  const [saveSegmentName, setSaveSegmentName] = useState("");
  const [saveSegmentBusy, setSaveSegmentBusy] = useState(false);
  const [saveSegmentError, setSaveSegmentError] = useState<string | null>(null);

  const scheduleRelativeHint = useMemo(() => {
    if (!scheduledLocal.trim()) {
      return null;
    }
    const wall = wallClockFromDatetimeLocal(scheduledLocal);
    if (!wall) {
      return null;
    }
    const when = wallClockInTimeZoneToDate(wall, tenantTimezone);
    if (!when) {
      return null;
    }
    return relativeFromNow(when, t);
  }, [scheduledLocal, tenantTimezone, t]);

  const scheduleBypassesTodayCap = useMemo(() => {
    if (!scheduledLocal.trim()) {
      return false;
    }
    const wall = wallClockFromDatetimeLocal(scheduledLocal);
    if (!wall) {
      return false;
    }
    const when = wallClockInTimeZoneToDate(wall, tenantTimezone);
    if (!when) {
      return false;
    }
    return isAfterUtcDayEnd(when);
  }, [scheduledLocal, tenantTimezone]);

  useEffect(() => {
    getContactFieldDefinitions({ per_page: 100 })
      .then((res) => {
        setFilterFieldDefs((res.data || []).filter((d) => d.is_filterable));
      })
      .catch(() => setFilterFieldDefs([]));

    fetchAllSegments()
      .then((list) => {
        setSegmentOptions(
          list.map((segment) => ({
            value: segment.id,
            label: segment.name,
          }))
        );
      })
      .catch(() => setSegmentOptions([]));
  }, []);

  useEffect(() => {
    if (!hasTenantRole("editor")) return;
    getWorkspaceSettings()
      .then((res) => setRequiresApproval(Boolean(res.data.send_approval_required)))
      .catch(() => setRequiresApproval(false));
  }, []);

  // Snapshot ramp + ritmo no /me — sem inventar teto/piso no front.
  useEffect(() => {
    fetchMe()
      .then((me) => {
        setRamp(me.tenant?.sending_ramp ?? null);
        const p = me.tenant?.sending_pace ?? null;
        setPace(p);
        if (p) {
          setIntervalSeconds(Math.max(p.min_interval_seconds, p.interval_seconds));
        }
      })
      .catch(() => {
        setRamp(null);
        setPace(null);
      });
  }, []);

  // Reply-To do setup: deixa claro para onde vão as respostas do cliente.
  useEffect(() => {
    let cancelled = false;
    getSendingSetup()
      .then((res) => {
        if (cancelled || !res?.success || !res.data) return;
        setReplyPreview({
          fromEmail: res.data.from_email,
          replyTo: res.data.reply_to,
        });
      })
      .catch(() => {
        if (!cancelled) setReplyPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Com “todos”, avisa se % frio ≥ limiar do tenant (LAC-129 P3).
  useEffect(() => {
    if (mode !== "all") {
      setShowInactiveHint(false);
      return;
    }
    let cancelled = false;
    getInactiveContactsSummary({ days: 90 })
      .then((res) => {
        if (cancelled || !res?.success || !res.data) {
          if (!cancelled) setShowInactiveHint(false);
          return;
        }
        const { eligible_percent, dashboard_threshold_percent } = res.data;
        setShowInactiveHint(
          eligible_percent >= (dashboard_threshold_percent ?? 20)
        );
      })
      .catch(() => {
        if (!cancelled) setShowInactiveHint(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const audienceExceedsRamp = useMemo(() => {
    if (
      estimated == null ||
      ramp?.remaining_today == null ||
      ramp.daily_cap == null
    ) {
      return false;
    }
    return estimated > ramp.remaining_today;
  }, [estimated, ramp]);

  // Cap do dia só bloqueia se ainda for “hoje” (agendar amanhã libera o HTTP).
  const showCapOverflow = audienceExceedsRamp && !scheduleBypassesTodayCap;

  // Amostra parcial faz sentido quando estoura o dia e ainda há restante > 0.
  const canPartialSample =
    showCapOverflow && (ramp?.remaining_today ?? 0) > 0;

  useEffect(() => {
    if (!canPartialSample && partialSample) {
      setPartialSample(false);
    }
  }, [canPartialSample, partialSample]);

  // Default do horário do restante = amanhã no mesmo relógio de parede.
  useEffect(() => {
    if (partialSample && resumeRemainderTomorrow && !resumeScheduledLocal.trim()) {
      setResumeScheduledLocal(tomorrowSameWallClockDatetimeLocal(tenantTimezone));
    }
  }, [
    partialSample,
    resumeRemainderTomorrow,
    resumeScheduledLocal,
    tenantTimezone,
  ]);

  const excludedEstimate = useMemo(() => {
    if (!partialSample || estimated == null || ramp?.remaining_today == null) {
      return null;
    }
    return Math.max(0, estimated - Math.min(estimated, ramp.remaining_today));
  }, [partialSample, estimated, ramp]);

  const remainderOverCap = useMemo(() => {
    if (excludedEstimate == null || ramp?.daily_cap == null) {
      return false;
    }
    return excludedEstimate > ramp.daily_cap;
  }, [excludedEstimate, ramp]);

  const partialSchedulePreview = useMemo(() => {
    if (!partialSample || estimated == null || ramp?.remaining_today == null) {
      return null;
    }
    const today = Math.min(estimated, ramp.remaining_today);
    const left = Math.max(0, estimated - today);
    const cap = ramp.daily_cap ?? left;
    if (left < 1) {
      return { today, tomorrow: 0, after: 0, days: 1 };
    }
    const tomorrow = Math.min(left, cap > 0 ? cap : left);
    const after = Math.max(0, left - tomorrow);
    const days = 1 + (left > 0 ? 1 : 0) + (after > 0 && cap > 0 ? Math.ceil(after / cap) : 0);
    return { today, tomorrow, after, days };
  }, [partialSample, estimated, ramp]);

  const dailyCapResetLocal = useMemo(
    () =>
      formatNextUtcDayResetLocal(
        tenantTimezone,
        i18n.language || "pt-BR"
      ),
    [tenantTimezone, i18n.language]
  );

  const minInterval = pace?.min_interval_seconds ?? 0;
  const maxInterval = pace?.max_interval_seconds ?? 3600;

  const scheduleForTomorrow = () => {
    setScheduledLocal(tomorrowSameWallClockDatetimeLocal(tenantTimezone));
    setPartialSample(false);
    setFormError(null);
    setFormErrorCode(null);
    toast.info(t("sending.modals.send.scheduleTomorrowDone"), {
      autoClose: 4000,
    });
  };

  const buildAudienceFilter = () => {
    const filter: NonNullable<SendCampaignPayload["audience"]["filter"]> = {};
    if (filterStatus) {
      filter.status = filterStatus;
    }
    if (filterTags.length > 0) {
      filter.tag_ids = filterTags.map((tag) => tag.value);
      filter.tag_match =
        filterTags.length > 1 ? filterTagMatch : undefined;
    }

    const fieldEntries: Record<string, FieldFilterEntry> = {};
    for (const row of filterFieldRows) {
      if (!row.slug) {
        continue;
      }
      const entry: FieldFilterEntry = {
        op: row.op,
        value: row.value,
        valueTo: row.valueTo,
      };
      if (isFieldFilterActive(entry)) {
        fieldEntries[row.slug] = entry;
      }
    }
    const fieldsParam = buildFieldsQueryParam(fieldEntries);
    if (fieldsParam) {
      filter.fields = fieldsParam;
    }

    const hasCriteria =
      !!filter.status ||
      (filter.tag_ids?.length ?? 0) > 0 ||
      (filter.fields && Object.keys(filter.fields).length > 0);
    return hasCriteria ? filter : null;
  };

  const addFilterFieldRow = () => {
    setFilterFieldRows((prev) => [
      ...prev,
      {
        key: nextFilterRowKey(),
        slug: "",
        op: "contains",
        value: "",
        valueTo: "",
      },
    ]);
  };

  const updateFilterFieldRow = (
    key: string,
    patch: Partial<FilterFieldRow>
  ) => {
    setFilterFieldRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) {
          return row;
        }
        const next = { ...row, ...patch };
        if (patch.slug !== undefined && patch.slug !== row.slug) {
          const def = filterFieldDefs.find((d) => d.slug === patch.slug);
          if (def) {
            next.op = defaultOpForFieldType(def.type);
            next.value = "";
            next.valueTo = "";
          }
        }
        return next;
      })
    );
  };

  const removeFilterFieldRow = (key: string) => {
    setFilterFieldRows((prev) => prev.filter((row) => row.key !== key));
  };

  const refreshEstimate = useCallback(async () => {
    if (mode === "tags" && selectedTags.length === 0) {
      setEstimated(null);
      return;
    }
    if (mode === "segment" && !selectedSegment) {
      setEstimated(null);
      return;
    }
    if (mode === "filter") {
      const filter = buildAudienceFilter();
      if (!filter) {
        setEstimated(null);
        return;
      }
    }
    setEstimating(true);
    try {
      const filterPayload =
        mode === "filter" ? buildAudienceFilter() ?? undefined : undefined;
      const res = await estimateCampaignAudience(campaign.id, {
        audience: {
          mode,
          tag_ids:
            mode === "tags" ? selectedTags.map((opt) => opt.value) : [],
          segment_id:
            mode === "segment" ? selectedSegment?.value ?? null : null,
          ...(mode === "filter" && filterPayload
            ? { filter: filterPayload }
            : {}),
        },
      });
      if (res?.success) {
        setEstimated(res.data.estimated);
      }
    } catch {
      setEstimated(null);
    } finally {
      setEstimating(false);
    }
  }, [
    campaign.id,
    mode,
    selectedTags,
    selectedSegment,
    filterStatus,
    filterTags,
    filterTagMatch,
    filterFieldRows,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      refreshEstimate();
    }, ESTIMATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [refreshEstimate]);

  const showSetupToast = (message: string) => {
    toast.error(
      <span>
        {message}{" "}
        <Link
          to="/settings/sending"
          className="text-decoration-underline fw-semibold"
        >
          {t("sending.toast.openSetup")}
        </Link>
      </span>,
      { autoClose: 8000 }
    );
  };

  const submit = async () => {
    setFormError(null);
    setFormErrorCode(null);
    setTagsError(null);
    setSegmentError(null);
    setFilterError(null);

    if (mode === "tags" && selectedTags.length === 0) {
      setTagsError(t("sending.modals.send.tagsRequired"));
      return;
    }

    if (mode === "segment" && !selectedSegment) {
      setSegmentError(t("sending.modals.send.segmentRequired"));
      return;
    }

    let audienceFilter: SendCampaignPayload["audience"]["filter"] | undefined;
    if (mode === "filter") {
      const filter = buildAudienceFilter();
      if (!filter) {
        setFilterError(t("sending.modals.send.filterRequired"));
        return;
      }
      audienceFilter = filter;
    }

    if (showCapOverflow && !partialSample) {
      setFormError(t("sending.modals.send.reduceAudienceOrPartial"));
      return;
    }

    let scheduledAt: string | null = null;
    if (scheduledLocal.trim()) {
      scheduledAt = wallClockFromDatetimeLocal(scheduledLocal);
      if (!scheduledAt) {
        setFormError(t("sending.form.scheduleInvalid"));
        return;
      }
      const when = wallClockInTimeZoneToDate(scheduledAt, tenantTimezone);
      if (when && when.getTime() < Date.now() - 60_000) {
        setFormError(t("sending.form.schedulePast"));
        return;
      }
    }

    let deadlineAt: string | null = null;
    if (deadlineLocal.trim()) {
      deadlineAt = wallClockFromDatetimeLocal(deadlineLocal);
      if (!deadlineAt) {
        setFormError(t("sending.form.deadlineInvalid"));
        return;
      }
    }

    const effectiveInterval = Math.max(minInterval, Math.min(intervalSeconds, maxInterval));

    setBusy(true);
    try {
      const res = await queueCampaignSend(campaign.id, {
        audience: {
          mode,
          tag_ids:
            mode === "tags" ? selectedTags.map((opt) => opt.value) : [],
          segment_id:
            mode === "segment" ? selectedSegment?.value ?? null : null,
          ...(mode === "filter" && audienceFilter
            ? { filter: audienceFilter }
            : {}),
        },
        partial_sample: partialSample || undefined,
        sample_by: partialSample
          ? sampleByEngagement
            ? "engagement"
            : "random"
          : undefined,
        resume_remainder_tomorrow:
          partialSample && resumeRemainderTomorrow ? true : undefined,
        resume_scheduled_at:
          partialSample && resumeRemainderTomorrow && resumeScheduledLocal.trim()
            ? wallClockFromDatetimeLocal(resumeScheduledLocal) || undefined
            : undefined,
        excluded_tag_id:
          partialSample && excludedTagOption
            ? excludedTagOption.value
            : undefined,
        // Tag dos excluídos fica ligada por padrão no backend quando parcial.
        scheduled_at: scheduledAt,
        send_interval_seconds: effectiveInterval,
        deadline_at: deadlineAt,
      });
      toast.success(
        partialSample
          ? res?.message || t("sending.toast.queuedPartial")
          : res?.message || t("sending.toast.queued"),
        { autoClose: 4000 }
      );
      onSuccess(res.data.id);
    } catch (e: any) {
      const code = e?.code as string | undefined;
      const apiMessage = (e?.message as string | undefined) || undefined;
      if (code === "SETUP_INCOMPLETE") {
        showSetupToast(apiMessage || t("sending.error.generic"));
      } else if (code === "DAILY_SEND_CAP") {
        setFormErrorCode(code);
        setFormError(apiMessage || t("sending.error.dailySendCap"));
        toast.error(apiMessage || t("sending.error.dailySendCap"), {
          autoClose: 8000,
        });
      } else if (code === "SECOND_STRIKE_ACTIVE") {
        // LAC-140: message da API já traz teto :max; reforça link help.
        const msg = apiMessage || t("sending.error.secondStrike");
        setFormErrorCode(code);
        setFormError(msg);
        toast.error(msg, { autoClose: 10000 });
      } else if (
        code === "QUOTA_EXCEEDED" ||
        code === "SUBSCRIPTION_INACTIVE" ||
        code === "CIRCUIT_OPEN" ||
        code === "PLATFORM_SEND_FROZEN"
      ) {
        setFormErrorCode(code);
        toast.error(apiMessage || t("sending.error.generic"), {
          autoClose: 5000,
        });
      } else if (
        code === "SCHEDULE_INVALID" ||
        code === "SCHEDULE_IN_PAST" ||
        code === "DEADLINE_INVALID" ||
        code === "DEADLINE_IN_PAST" ||
        code === "SEND_INTERVAL_BELOW_FLOOR"
      ) {
        setFormErrorCode(code);
        setFormError(apiMessage || t("sending.form.scheduleInvalid"));
      } else {
        setFormErrorCode(code || null);
        setFormError(apiMessage || t("sending.error.send"));
      }
    } finally {
      setBusy(false);
    }
  };

  const estimateLabel = useMemo(() => {
    if (estimating) {
      return null;
    }
    if (estimated === null) {
      return t("sending.form.estimateUnavailable");
    }
    if (partialSample && ramp?.remaining_today != null) {
      const sample = Math.min(estimated, ramp.remaining_today);
      return t("sending.form.estimatePartial", {
        sample,
        total: estimated,
      });
    }
    return t("sending.form.estimate", { count: estimated });
  }, [estimating, estimated, partialSample, ramp, t]);

  const durationHint = useMemo(() => {
    if (estimated == null || estimated < 1) {
      return null;
    }
    const effective = Math.max(minInterval, Math.min(intervalSeconds, maxInterval));
    if (effective <= 0) {
      return null;
    }
    const count =
      partialSample && ramp?.remaining_today != null
        ? Math.min(estimated, ramp.remaining_today)
        : estimated;
    if (count < 2) {
      return null;
    }
    return formatDurationHint((count - 1) * effective, t);
  }, [
    estimated,
    intervalSeconds,
    maxInterval,
    minInterval,
    partialSample,
    ramp,
    t,
  ]);

  const requestSubmit = () => {
    if (partialSample) {
      const remaining = ramp?.remaining_today ?? 0;
      const total = estimated ?? 0;
      const ok = window.confirm(
        t("sending.modals.send.partialSampleConfirmDialog", {
          remaining,
          total,
        })
      );
      if (!ok) {
        return;
      }
    }
    void submit();
  };

  const canSaveFilterAsSegment = useMemo(() => {
    if (mode !== "filter") {
      return false;
    }
    return buildAudienceFilter() !== null;
  }, [
    mode,
    filterStatus,
    filterTags,
    filterTagMatch,
    filterFieldRows,
  ]);

  const submitSaveFilterAsSegment = async () => {
    const name = saveSegmentName.trim();
    if (!name) {
      setSaveSegmentError(t("sending.modals.send.saveSegmentNameRequired"));
      return;
    }
    const filter = buildAudienceFilter();
    if (!filter) {
      setSaveSegmentError(t("sending.modals.send.filterRequired"));
      return;
    }
    const rules = buildSegmentRulesFromAudienceFilter({
      status: filter.status,
      tag_ids: filter.tag_ids,
      tag_match: filter.tag_match,
      fields: filter.fields,
    });
    if (!rules) {
      setSaveSegmentError(t("sending.modals.send.filterRequired"));
      return;
    }
    setSaveSegmentBusy(true);
    setSaveSegmentError(null);
    try {
      const res = await createSegment({ name, rules });
      if (!res?.success) {
        setSaveSegmentError(t("sending.modals.send.saveSegmentError"));
        return;
      }
      const segmentId = res.data?.id;
      setSaveSegmentOpen(false);
      setSaveSegmentName("");
      toast.success(
        <span>
          {t("sending.modals.send.saveSegmentSuccess")}{" "}
          {segmentId ? (
            <Link
              to={`/segments?edit=${segmentId}`}
              className="text-white text-decoration-underline"
            >
              {t("sending.modals.send.saveSegmentOpen")}
            </Link>
          ) : null}
        </span>
      );
      if (segmentId) {
        navigate(`/segments?edit=${segmentId}`);
      }
    } catch (e: any) {
      setSaveSegmentError(
        e?.message || t("sending.modals.send.saveSegmentError")
      );
    } finally {
      setSaveSegmentBusy(false);
    }
  };

  return (
    <>
      <p className="text-muted mb-3">
        {t("sending.modals.send.help", { name: campaign.name })}
      </p>
      <Alert color="warning" className="mb-3 py-2">
        {t("sending.modals.send.listQualityHint")}{" "}
        <a
          href={sendListHelpHref}
          target="_blank"
          rel="noopener noreferrer"
          className="fw-medium"
        >
          {t("sending.modals.send.listQualityHelp")}
        </a>
      </Alert>
      {replyPreview ? (
        <Alert
          color={replyPreview.replyTo ? "info" : "warning"}
          className="mb-3 py-2"
        >
          {replyPreview.replyTo
            ? t("sending.modals.send.repliesGoTo", {
                email: replyPreview.replyTo,
              })
            : t("sending.modals.send.repliesGoToFrom", {
                email: replyPreview.fromEmail || "—",
              })}{" "}
          <Link to="/settings/sending" className="fw-medium">
            {t("sending.modals.send.repliesSetupLink")}
          </Link>
        </Alert>
      ) : null}
      {requiresApproval && (
        <Alert color="info" className="mb-3 py-2">
          <strong>{t("sending.approval.editorNoticeTitle")}</strong>{" "}
          {t("sending.approval.editorNotice")}
        </Alert>
      )}
      {ramp &&
        (ramp.stage === "new" || ramp.stage === "warming") &&
        ramp.daily_cap != null && (
          <Alert color="info" className="mb-3 py-2">
            <Trans
              i18nKey="sending.modals.send.dailyCapHint"
              values={{
                sent: ramp.sent_today,
                cap: ramp.daily_cap,
                remaining: ramp.remaining_today ?? 0,
                resetAt: dailyCapResetLocal,
              }}
              components={{ strong: <strong /> }}
            />
          </Alert>
        )}
      {showCapOverflow && (
        <Alert color="warning" className="mb-3 py-2">
          <p className="mb-2">
            <Trans
              i18nKey="sending.modals.send.reduceAudienceHint"
              values={{ resetAt: dailyCapResetLocal }}
              components={{ strong: <strong /> }}
            />
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Button
              color="warning"
              size="sm"
              outline
              disabled={busy}
              onClick={() => {
                setMode("tags");
                toast.info(t("sending.modals.send.useTagsHint"), {
                  autoClose: 4000,
                });
              }}
            >
              {t("sending.modals.send.useTagsCta")}
            </Button>
            <Button
              color="warning"
              size="sm"
              outline
              disabled={busy}
              onClick={scheduleForTomorrow}
            >
              {t("sending.modals.send.scheduleTomorrow")}
            </Button>
          </div>
          {canPartialSample && (
            <div className="form-check mt-3 mb-0">
              <Input
                className="form-check-input"
                type="checkbox"
                id={`send-partial-${campaign.id}-${compact ? "m" : "p"}`}
                checked={partialSample}
                disabled={busy}
                onChange={(e) => setPartialSample(e.target.checked)}
              />
              <Label
                className="form-check-label"
                htmlFor={`send-partial-${campaign.id}-${compact ? "m" : "p"}`}
              >
                {t("sending.modals.send.partialSampleLabel", {
                  remaining: ramp?.remaining_today ?? 0,
                })}
              </Label>
              <p className="text-muted small mb-0 mt-1">
                {t("sending.modals.send.partialSampleHelp")}
              </p>
              {partialSample && (
                <div className="ms-1 mt-2">
                  <div className="form-check mb-2">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`send-partial-eng-${campaign.id}-${compact ? "m" : "p"}`}
                      checked={sampleByEngagement}
                      disabled={busy}
                      onChange={(e) => setSampleByEngagement(e.target.checked)}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`send-partial-eng-${campaign.id}-${compact ? "m" : "p"}`}
                    >
                      {t("sending.modals.send.partialSampleByEngagement")}
                    </Label>
                  </div>
                  <div className="form-check mb-0">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`send-partial-resume-${campaign.id}-${compact ? "m" : "p"}`}
                      checked={resumeRemainderTomorrow}
                      disabled={busy}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setResumeRemainderTomorrow(on);
                        if (on && !resumeScheduledLocal.trim()) {
                          setResumeScheduledLocal(
                            tomorrowSameWallClockDatetimeLocal(tenantTimezone)
                          );
                        }
                      }}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`send-partial-resume-${campaign.id}-${compact ? "m" : "p"}`}
                    >
                      {t("sending.modals.send.partialResumeTomorrow")}
                    </Label>
                    <p className="text-muted small mb-0 mt-1">
                      {t("sending.modals.send.partialResumeTomorrowHelp")}
                    </p>
                  </div>
                  {resumeRemainderTomorrow && (
                    <div className="mt-2">
                      <Label
                        className="form-label small mb-1"
                        htmlFor={`send-partial-resume-at-${campaign.id}-${compact ? "m" : "p"}`}
                      >
                        {t("sending.modals.send.partialResumeAt")}
                      </Label>
                      <Input
                        id={`send-partial-resume-at-${campaign.id}-${compact ? "m" : "p"}`}
                        type="datetime-local"
                        value={resumeScheduledLocal}
                        disabled={busy}
                        onChange={(e) => setResumeScheduledLocal(e.target.value)}
                      />
                    </div>
                  )}
                  {remainderOverCap && excludedEstimate != null && (
                    <Alert color="warning" className="mt-2 mb-0 py-2 small">
                      {t("sending.modals.send.partialRemainderOverCap", {
                        excluded: excludedEstimate,
                        cap: ramp?.daily_cap ?? 0,
                      })}
                    </Alert>
                  )}
                  {partialSchedulePreview && (
                    <p className="text-muted small mb-0 mt-2">
                      {t("sending.modals.send.partialSchedulePreview", {
                        today: partialSchedulePreview.today,
                        tomorrow: partialSchedulePreview.tomorrow,
                        after: partialSchedulePreview.after,
                        days: partialSchedulePreview.days,
                      })}
                    </p>
                  )}
                  <div className="mt-2">
                    <Label className="form-label small mb-1">
                      {t("sending.modals.send.partialExcludedTagOptional")}
                    </Label>
                    <AsyncSelect
                      classNamePrefix="select2-selection"
                      isClearable
                      cacheOptions
                      defaultOptions
                      loadOptions={loadTagOptions}
                      value={excludedTagOption}
                      onChange={(opt: TagOption | null) =>
                        setExcludedTagOption(opt)
                      }
                      placeholder={t(
                        "sending.modals.send.partialExcludedTagPlaceholder"
                      )}
                      isDisabled={busy}
                    />
                    <p className="text-muted small mb-0 mt-1">
                      {t("sending.modals.send.partialExcludedTagHelp")}
                    </p>
                    {(excludedTagOption?.contacts_count ?? 0) >= 50 && (
                      <Alert color="warning" className="mt-2 mb-0 py-2 small">
                        {t("sending.modals.send.partialExcludedTagCrowded", {
                          count: excludedTagOption?.contacts_count ?? 0,
                        })}
                      </Alert>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Alert>
      )}
      {scheduleBypassesTodayCap && audienceExceedsRamp && (
        <Alert color="success" className="mb-3 py-2">
          {t("sending.modals.send.scheduleTomorrowReady")}
        </Alert>
      )}
      {partialSample && (
        <Alert color="info" className="mb-3 py-2">
          {t("sending.modals.send.partialSampleConfirm", {
            remaining: ramp?.remaining_today ?? 0,
            total: estimated ?? 0,
          })}
        </Alert>
      )}
      {formError && (
        <Alert color="danger" className="mb-3">
          {formError}
          {formErrorCode === "SECOND_STRIKE_ACTIVE" ? (
            <div className="mt-2">
              <a
                href={helpArticleUrl("/sends/why-paused", i18n.language || "pt-BR")}
                target="_blank"
                rel="noreferrer"
              >
                {t("sending.error.secondStrikeHelp")}
              </a>
            </div>
          ) : null}
        </Alert>
      )}
      <div className="mb-3">
        <Label className="form-label">{t("sending.modals.send.audience")}</Label>
        <Alert color="secondary" className="mb-2 py-2 small">
          <Trans
            i18nKey="sending.dispatch.hardBounceListHint"
            components={{
              contactsLink: <Link to="/contacts?hard_bounce_flagged=1" />,
            }}
          />
        </Alert>
        <div className="form-check mb-2">
          <Input
            className="form-check-input"
            type="radio"
            id={`send-audience-all-${campaign.id}-${compact ? "m" : "p"}`}
            checked={mode === "all"}
            disabled={busy}
            onChange={() => setMode("all")}
          />
          <Label
            className="form-check-label"
            htmlFor={`send-audience-all-${campaign.id}-${compact ? "m" : "p"}`}
          >
            {t("sending.modals.send.audienceAll")}
          </Label>
        </div>
        {mode === "all" && showInactiveHint && (
          <Alert color="warning" className="mb-2 py-2 small">
            <Trans
              i18nKey="sending.form.inactiveHint"
              components={{
                inactiveLink: <Link to="/contacts/inactive" />,
                segmentsLink: <Link to="/segments" />,
              }}
            />
          </Alert>
        )}
        <div className="form-check">
          <Input
            className="form-check-input"
            type="radio"
            id={`send-audience-tags-${campaign.id}-${compact ? "m" : "p"}`}
            checked={mode === "tags"}
            disabled={busy}
            onChange={() => setMode("tags")}
          />
          <Label
            className="form-check-label"
            htmlFor={`send-audience-tags-${campaign.id}-${compact ? "m" : "p"}`}
          >
            {t("sending.modals.send.audienceTags")}
          </Label>
        </div>
        <div className="form-check mt-2">
          <Input
            className="form-check-input"
            type="radio"
            id={`send-audience-filter-${campaign.id}-${compact ? "m" : "p"}`}
            checked={mode === "filter"}
            disabled={busy}
            onChange={() => setMode("filter")}
          />
          <Label
            className="form-check-label"
            htmlFor={`send-audience-filter-${campaign.id}-${compact ? "m" : "p"}`}
          >
            {t("sending.modals.send.audienceFilter")}
          </Label>
        </div>
        <div className="form-check mt-2">
          <Input
            className="form-check-input"
            type="radio"
            id={`send-audience-segment-${campaign.id}-${compact ? "m" : "p"}`}
            checked={mode === "segment"}
            disabled={busy}
            onChange={() => setMode("segment")}
          />
          <Label
            className="form-check-label"
            htmlFor={`send-audience-segment-${campaign.id}-${compact ? "m" : "p"}`}
          >
            {t("sending.modals.send.audienceSegment")}
          </Label>
        </div>
      </div>
      {mode === "tags" && (
        <div className="mb-3">
          <Label className="form-label">
            {t("sending.modals.send.tagsLabel")}
          </Label>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadTagOptions}
            value={selectedTags}
            onChange={(opts: readonly TagOption[] | null) =>
              setSelectedTags([...(opts || [])])
            }
            placeholder={t("sending.modals.send.tagsPlaceholder")}
            classNamePrefix="select"
            isDisabled={busy}
          />
          {tagsError && (
            <FormFeedback className="d-block">{tagsError}</FormFeedback>
          )}
        </div>
      )}
      {mode === "filter" && (
        <div className="mb-3 border rounded p-3 bg-light-subtle">
          <Row className="g-2">
            <Col md={4}>
              <Label className="form-label">
                {t("sending.modals.send.filterStatus")}
              </Label>
              <Input
                type="select"
                value={filterStatus}
                disabled={busy}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">
                  {t("sending.modals.send.filterStatusAll")}
                </option>
                <option value="active">active</option>
                <option value="quarantine">quarantine</option>
              </Input>
            </Col>
            <Col md={8}>
              <Label className="form-label">
                {t("sending.modals.send.tagsLabel")}
              </Label>
              <AsyncSelect
                isMulti
                cacheOptions
                defaultOptions
                loadOptions={loadTagOptions}
                value={filterTags}
                onChange={(opts: readonly TagOption[] | null) =>
                  setFilterTags([...(opts || [])])
                }
                placeholder={t("sending.modals.send.tagsPlaceholder")}
                classNamePrefix="select"
                isDisabled={busy}
              />
            </Col>
            {filterTags.length > 1 && (
              <Col md={4}>
                <Label className="form-label">
                  {t("sending.modals.send.filterTagMatch")}
                </Label>
                <Input
                  type="select"
                  value={filterTagMatch}
                  disabled={busy}
                  onChange={(e) =>
                    setFilterTagMatch(
                      e.target.value === "and" ? "and" : "or"
                    )
                  }
                >
                  <option value="or">
                    {t("sending.modals.send.filterTagMatchOr")}
                  </option>
                  <option value="and">
                    {t("sending.modals.send.filterTagMatchAnd")}
                  </option>
                </Input>
              </Col>
            )}
          </Row>

          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <Label className="form-label mb-0">
                {t("sending.modals.send.filterFields")}
              </Label>
              <Button
                type="button"
                color="soft-primary"
                size="sm"
                disabled={busy || filterFieldDefs.length === 0}
                onClick={addFilterFieldRow}
              >
                {t("sending.modals.send.filterFieldAdd")}
              </Button>
            </div>
            {filterFieldRows.length === 0 ? (
              <p className="text-muted small mb-0">
                {t("sending.modals.send.filterFieldEmpty")}
              </p>
            ) : (
              filterFieldRows.map((row) => {
                const def = filterFieldDefs.find((d) => d.slug === row.slug);
                const ops = def
                  ? operatorsForFieldType(def.type)
                  : (["eq", "contains"] as FieldFilterOp[]);

                return (
                  <Row key={row.key} className="g-2 mb-2 align-items-end">
                    <Col md={3}>
                      <Input
                        type="select"
                        value={row.slug}
                        disabled={busy}
                        onChange={(e) =>
                          updateFilterFieldRow(row.key, { slug: e.target.value })
                        }
                      >
                        <option value="">
                          {t("sending.modals.send.filterFieldNone")}
                        </option>
                        {filterFieldDefs.map((fieldDef) => (
                          <option key={fieldDef.id} value={fieldDef.slug}>
                            {fieldDef.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    {row.slug && (
                      <>
                        <Col md={2}>
                          <Input
                            type="select"
                            value={row.op}
                            disabled={busy}
                            onChange={(e) =>
                              updateFilterFieldRow(row.key, {
                                op: e.target.value as FieldFilterOp,
                              })
                            }
                          >
                            {ops.map((op) => (
                              <option key={op} value={op}>
                                {operatorLabel(op, t)}
                              </option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={5}>
                          {def?.type === "select" || def?.type === "multiselect" ? (
                            row.op === "in" ? (
                              <Select
                                isMulti
                                options={(def.options || []).map((opt) => ({
                                  value: opt,
                                  label: opt,
                                }))}
                                value={parseInValues(row.value).map((v) => ({
                                  value: v,
                                  label: v,
                                }))}
                                onChange={(
                                  opts: readonly { value: string; label: string }[] | null
                                ) =>
                                  updateFilterFieldRow(row.key, {
                                    value: (opts || [])
                                      .map((o) => o.value)
                                      .join(", "),
                                  })
                                }
                                classNamePrefix="select"
                                isDisabled={busy}
                                placeholder={t(
                                  "sending.modals.send.filterFieldValue"
                                )}
                              />
                            ) : (
                              <Input
                                type="select"
                                value={row.value}
                                disabled={busy}
                                onChange={(e) =>
                                  updateFilterFieldRow(row.key, {
                                    value: e.target.value,
                                  })
                                }
                              >
                                <option value="">
                                  {t("sending.modals.send.filterFieldValue")}
                                </option>
                                {(def.options || []).map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Input>
                            )
                          ) : def?.type === "boolean" ? (
                            <Input
                              type="select"
                              value={row.value}
                              disabled={busy}
                              onChange={(e) =>
                                updateFilterFieldRow(row.key, {
                                  value: e.target.value,
                                })
                              }
                            >
                              <option value="">
                                {t("sending.modals.send.filterFieldValue")}
                              </option>
                              <option value="true">
                                {t("contacts.fields.booleanTrue")}
                              </option>
                              <option value="false">
                                {t("contacts.fields.booleanFalse")}
                              </option>
                            </Input>
                          ) : row.op === "between" ? (
                            <div className="d-flex gap-1">
                              <Input
                                type={
                                  def?.type === "date"
                                    ? "date"
                                    : def?.type === "number"
                                      ? "number"
                                      : "text"
                                }
                                value={row.value}
                                disabled={busy}
                                onChange={(e) =>
                                  updateFilterFieldRow(row.key, {
                                    value: e.target.value,
                                  })
                                }
                              />
                              <Input
                                type={
                                  def?.type === "date"
                                    ? "date"
                                    : def?.type === "number"
                                      ? "number"
                                      : "text"
                                }
                                value={row.valueTo ?? ""}
                                disabled={busy}
                                placeholder={t("contacts.filter.operator.to")}
                                onChange={(e) =>
                                  updateFilterFieldRow(row.key, {
                                    valueTo: e.target.value,
                                  })
                                }
                              />
                            </div>
                          ) : (
                            <Input
                              type={
                                def?.type === "date"
                                  ? "date"
                                  : def?.type === "number"
                                    ? "number"
                                    : "text"
                              }
                              value={row.value}
                              disabled={busy}
                              placeholder={t(
                                "sending.modals.send.filterFieldValue"
                              )}
                              onChange={(e) =>
                                updateFilterFieldRow(row.key, {
                                  value: e.target.value,
                                })
                              }
                            />
                          )}
                        </Col>
                      </>
                    )}
                    <Col md="auto">
                      <Button
                        type="button"
                        color="light"
                        size="sm"
                        disabled={busy}
                        onClick={() => removeFilterFieldRow(row.key)}
                        aria-label={t("sending.modals.send.filterFieldRemove")}
                      >
                        <i className="ri-close-line" />
                      </Button>
                    </Col>
                  </Row>
                );
              })
            )}
          </div>

          {filterError && (
            <FormFeedback className="d-block mt-2">{filterError}</FormFeedback>
          )}

          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button
              type="button"
              color="soft-primary"
              size="sm"
              disabled={busy || !canSaveFilterAsSegment}
              onClick={() => {
                setSaveSegmentName("");
                setSaveSegmentError(null);
                setSaveSegmentOpen(true);
              }}
            >
              {t("sending.modals.send.saveAsSegment")}
            </Button>
          </div>
        </div>
      )}
      {mode === "segment" && (
        <div className="mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-1">
            <Label className="form-label mb-0">
              {t("sending.modals.send.segmentLabel")}
            </Label>
            {/* Sempre visível: empty → criar; com lista → editar regras sem sair do disparo. */}
            <Link to="/segments" className="small">
              {segmentOptions.length === 0
                ? t("sending.modals.send.segmentEmptyCta")
                : t("sending.modals.send.manageSegments")}
            </Link>
          </div>
          <Select
            options={segmentOptions}
            value={selectedSegment}
            onChange={(opt: SegmentOption | null) => setSelectedSegment(opt)}
            placeholder={t("sending.modals.send.segmentPlaceholder")}
            classNamePrefix="select"
            isDisabled={busy}
            isClearable
          />
          {segmentOptions.length === 0 && (
            <Alert color="info" className="py-2 mt-2 mb-0">
              <p className="mb-2">{t("sending.modals.send.segmentEmptyTitle")}</p>
              <Button color="primary" size="sm" tag={Link} to="/segments">
                {t("sending.modals.send.segmentEmptyCta")}
              </Button>
            </Alert>
          )}
          {segmentError && (
            <FormFeedback className="d-block">{segmentError}</FormFeedback>
          )}
        </div>
      )}
      <div className="mb-3">
        <Label className="form-label" htmlFor={`send-schedule-${campaign.id}`}>
          {t("sending.form.scheduleLabel")}
        </Label>
        <Input
          id={`send-schedule-${campaign.id}`}
          type="datetime-local"
          value={scheduledLocal}
          disabled={busy}
          onChange={(e) => setScheduledLocal(e.target.value)}
        />
        <p className="text-muted small mb-0 mt-1">
          {t("sending.form.scheduleHelp")}
        </p>
        <p className="text-muted small mb-0 mt-1">
          {t("sending.form.scheduleTimezone", { timezone: timezoneLabel })}
        </p>
        {scheduleRelativeHint && (
          <p className="text-muted small mb-0 mt-1 fw-medium">
            {scheduleRelativeHint}
          </p>
        )}
      </div>
      <div className="mb-3">
        <Label className="form-label" htmlFor={`send-deadline-${campaign.id}`}>
          {t("sending.form.deadlineLabel")}
        </Label>
        <Input
          id={`send-deadline-${campaign.id}`}
          type="datetime-local"
          value={deadlineLocal}
          disabled={busy}
          onChange={(e) => setDeadlineLocal(e.target.value)}
        />
        <p className="text-muted small mb-0 mt-1">
          {t("sending.form.deadlineHelp")}
        </p>
      </div>
      <div className="mb-3">
        <Label className="form-label" htmlFor={`send-interval-${campaign.id}`}>
          {t("sending.form.intervalLabel")}
        </Label>
        <Input
          id={`send-interval-${campaign.id}`}
          type="select"
          value={String(intervalSeconds)}
          disabled={busy}
          onChange={(e) => {
            const next = Number(e.target.value);
            setIntervalSeconds(Math.max(minInterval, next));
          }}
        >
          {INTERVAL_PRESETS.filter((s) => s >= minInterval && s <= maxInterval).map(
            (s) => (
              <option key={s} value={s}>
                {formatIntervalOption(s, t)}
              </option>
            )
          )}
          {minInterval > 0 &&
            !INTERVAL_PRESETS.includes(minInterval as (typeof INTERVAL_PRESETS)[number]) && (
              <option value={minInterval}>
                {formatIntervalOption(minInterval, t)}
              </option>
            )}
        </Input>
        <p className="text-muted small mb-0 mt-1">
          {minInterval > 0
            ? t("sending.form.intervalFloorHelp", { min: minInterval })
            : t("sending.form.intervalHelp")}
        </p>
        {durationHint && (
          <p className="text-muted small mb-0 mt-1 fw-medium">
            {t("sending.form.durationHint", { duration: durationHint })}
          </p>
        )}
      </div>
      <Alert color="info" className="mb-3 py-2">
        {estimating ? (
          <span className="d-inline-flex align-items-center gap-2">
            <Spinner size="sm" /> {t("sending.form.estimating")}
          </span>
        ) : (
          estimateLabel
        )}
      </Alert>
      <div
        className={
          compact
            ? "d-flex justify-content-end gap-2"
            : "d-flex flex-wrap gap-2"
        }
      >
        {onCancel && (
          <Button color="light" disabled={busy} onClick={onCancel}>
            {t("sending.modals.cancel")}
          </Button>
        )}
        <Button
          color="primary"
          disabled={busy || (showCapOverflow && !partialSample)}
          onClick={requestSubmit}
        >
          {busy ? <Spinner size="sm" /> : t("sending.modals.send.submit")}
        </Button>
      </div>

      <Modal
        isOpen={saveSegmentOpen}
        toggle={() => !saveSegmentBusy && setSaveSegmentOpen(false)}
      >
        <ModalHeader toggle={() => !saveSegmentBusy && setSaveSegmentOpen(false)}>
          {t("sending.modals.send.saveAsSegmentTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">{t("sending.modals.send.saveAsSegmentHelp")}</p>
          <Label className="form-label" htmlFor="send-save-segment-name">
            {t("sending.modals.send.saveAsSegmentName")}
          </Label>
          <Input
            id="send-save-segment-name"
            value={saveSegmentName}
            disabled={saveSegmentBusy}
            onChange={(e) => setSaveSegmentName(e.target.value)}
          />
          {saveSegmentError ? (
            <FormFeedback className="d-block">{saveSegmentError}</FormFeedback>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={saveSegmentBusy}
            onClick={() => setSaveSegmentOpen(false)}
          >
            {t("sending.modals.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={saveSegmentBusy}
            onClick={() => void submitSaveFilterAsSegment()}
          >
            {saveSegmentBusy ? (
              <Spinner size="sm" />
            ) : (
              t("sending.modals.send.saveAsSegmentSubmit")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SendCampaignForm;
